















































































































import {
	defineComponent, PropType,
} from '@vue/composition-api';
import { v4 } from 'uuid';
import { ButtonPalettes } from '@front-app/components/buttons/palettes';
import { EventManagementData,	EventNotification, EventNotificationType } from '@front-app/services/EventsApi/EventModel';
import { formatAddress } from '@front-app/util/format';
import { checkIfEventManagementDataOnlineMeetingIsEmpty } from '@front-app/modules/event/util/location';
import { onlineMeetingButtonTextValidator, onlineMeetingUrlValidator } from '@front-app/modules/event/util/validation';
import { Size, Spacing } from '@front-app/components/constants';
import { syncedRef } from '@front-app/util/reactivity';
import { useAsyncStatus } from '@front-app/hooks/useAsyncStatus';
import { useContrast } from '@front-app/hooks/useContrast';
import { useEventAdminStore } from '@front-app/modules/event/store/EventAdmin';
import { useFocusFirstInvalidField } from '@front-app/hooks/useFocusFirstInvalidField';
import { v } from '@front-app/components/form/validation/useValidation';
import AddressSection from '@front-app/components/form/AddressSection.vue';
import ColorPicker from '@front-app/components/inputs/ColorPicker.vue';
import CtaButton from '@front-app/components/buttons/CtaButton.vue';
import EditEventBaseForm from '@front-app/modules/event/components/form/EditEventBaseForm.vue';
import EventMeetingButton from '@front-app/modules/event/components/EventMeetingButton.vue';
import FormError from '@front-app/components/form/FormError.vue';
import FormLabel from '@front-app/components/form/FormLabel.vue';
import FormSection from '@front-app/components/form/FormSection.vue';
import SimpleInputSection from '@front-app/components/form/SimpleInputSection.vue';
import TrCheckbox from '@front-app/components/inputs/Checkbox.vue';
import SimpleCard from '@front-app/components/cards/SimpleCard.vue';
import { updateEventNotification } from '@front-app/services/EventsApi/EventsApi';

export default defineComponent({
	components: {
		AddressSection,
		ColorPicker,
		CtaButton,
		EditEventBaseForm,
		EventMeetingButton,
		FormError,
		FormLabel,
		FormSection,
		SimpleInputSection,
		TrCheckbox,
		SimpleCard,
	},
	props: {
		eventData: {
			type: Object as PropType<EventManagementData>,
			required: true,
		},
		eventReminder: {
			type: Object as PropType<EventNotification>,
			required: true,
		},
	},
	setup(props, ctx) {
		// In-person location
		const inPersonLocation = syncedRef(() => props.eventData.location).ref;
		// END In-person location

		// Event reminder URL
		const {
			track: trackUpdateEventReminder,
			isError: isErrorUpdatingEventReminder,
			isLoading: isUpdatingEventReminder,
			isSuccess: isSuccessUpdatingEventReminder,
		} = useAsyncStatus();
		const isReminderEventUrlEnabled = syncedRef(() => props.eventReminder.metadata?.isReminderEventUrlEnabled || false).ref;
		const toggleReminderEventUrl = async () => {
			isReminderEventUrlEnabled.value = !isReminderEventUrlEnabled.value;
		};
		// END Event reminder URL

		// Online location
		const onlineMeeting = syncedRef(() => checkIfEventManagementDataOnlineMeetingIsEmpty(props.eventData.onlineMeeting)).ref;
		const toggleOnlineLocation = () => {
			onlineMeeting.value.isEnabled = !onlineMeeting.value.isEnabled;

			// Turn off the event URL in reminder email if online meeting is being turned off
			if (!onlineMeeting.value.isEnabled && isReminderEventUrlEnabled.value) {
				isReminderEventUrlEnabled.value = false;
			}
		};
		const {
			contrastIsAcceptable: isOnlineMeetingButtonContrastSufficient,
			test,
			elRef: meetingButtonRef,
		} = useContrast();
		const setOnlineMeetingButtonColor = async (val: string) => {
			onlineMeeting.value.videoButtonColor = val;
			test();
		};
		// END Online location

		// Validation
		const validation = v({
			onlineMeetingUrl: {
				value: onlineMeeting,
				rules: [onlineMeetingUrlValidator],
			},
			onlineMeetingButtonText: {
				value: onlineMeeting,
				rules: [onlineMeetingButtonTextValidator],
			},
		});

		const formId = `event-form-${v4()}`;
		const { focusFirstInvalid } = useFocusFirstInvalidField({ containerSelector: `#${formId}` });
		// END Validation

		// Saving
		const {
			track,
			isLoading: isSavingUpdates,
			isError: isErrorSavingUpdates,
			isSuccess: isSuccessSavingUpdates,
		} = useAsyncStatus();

		const saveUpdates = async () => {
			validation.validate();
			if (!validation.isValid.value) {
				focusFirstInvalid();
				return;
			}

			const formattedInPersonLocation = formatAddress(inPersonLocation.value);

			await track(useEventAdminStore().update(props.eventData.id, {
				location: formattedInPersonLocation,
				onlineMeeting: onlineMeeting.value,
			}));

			// Save the state of the toggle for event reminder URL
			const updatedReminder = await trackUpdateEventReminder(updateEventNotification(
				props.eventData.id,
				EventNotificationType.REMINDER,
				{ isReminderEventUrlEnabled: isReminderEventUrlEnabled.value },
			));

			if (isSuccessUpdatingEventReminder.value) {
				ctx.emit('update-reminder', updatedReminder);
			}

			if (isSuccessSavingUpdates.value && isSuccessUpdatingEventReminder.value) {
				ctx.emit('close');
			}
		};
		// END Saving

		return {
			// In-person location
			inPersonLocation,
			// Online location
			onlineMeeting,
			toggleOnlineLocation,
			isOnlineMeetingButtonContrastSufficient,
			setOnlineMeetingButtonColor,
			meetingButtonRef,
			// Event reminder URL
			isReminderEventUrlEnabled,
			toggleReminderEventUrl,
			isErrorUpdatingEventReminder,
			isUpdatingEventReminder,
			// Saving
			formId,
			validation,
			isSavingUpdates,
			isErrorSavingUpdates,
			saveUpdates,
			// Util
			Size,
			Spacing,
			ButtonPalettes,
		};
	},
});
